import { createBrowserRouter, redirect, Navigate } from 'react-router-dom';
import { PublicLayout } from '@/presentation/layout/PublicLayout';
import { AdminLayout } from '@/presentation/layout/AdminLayout';
import { LoginPage } from '@/presentation/page/auth/LoginPage';
import { SignupPage } from '@/presentation/page/auth/SignupPage';
import { useAuthStore } from '@/infrastructure/store/authStore';

import { HomePage } from '@/presentation/page/catalog/HomePage';
import { FormationPage } from '@/presentation/page/catalog/FormationPage';
import { CategoryViewPage } from '@/presentation/page/catalog/CategoryViewPage';
import { SubcategoryViewPage } from '@/presentation/page/catalog/SubcategoryViewPage';
import { CourseViewPage } from '@/presentation/page/course/CourseViewPage';
import { ContactPage } from '@/presentation/page/contact/ContactPage';
import { PaymentSuccessPage } from '@/presentation/page/payment/PaymentSuccessPage';
import { PaymentCancelPage } from '@/presentation/page/payment/PaymentCancelPage';
import { ErrorBoundary } from '@/presentation/component/ui';

import { AdminDashboardPage } from '@/presentation/page/admin/AdminDashboardPage';
import { AdminCategoriesPage } from '@/presentation/page/admin/AdminCategoriesPage';
import { AdminSubcategoriesPage } from '@/presentation/page/admin/AdminSubcategoriesPage';
import { AdminChaptersPage } from '@/presentation/page/admin/AdminChaptersPage';
import { AdminCoursesPage } from '@/presentation/page/admin/AdminCoursesPage';
import { AdminUsersPage } from '@/presentation/page/admin/AdminUsersPage';

const authRedirectLoader = async () => {
  const { checkAuth } = useAuthStore.getState();
  try {
    await checkAuth();
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      return redirect('/formation');
    }
  } catch {
    // token invalid — allow access
  }
  return null;
};

const adminLoader = async () => {
  const { checkAuth } = useAuthStore.getState();
  try {
    await checkAuth();
    const { isAuthenticated, user } = useAuthStore.getState();
    if (!isAuthenticated || !user) {
      return redirect(`/login?from=${encodeURIComponent('/admin')}`);
    }
    if (user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') {
      return redirect('/?accessDenied');
    }
    return user;
  } catch {
    return redirect(`/login?from=${encodeURIComponent('/admin')}`);
  }
};

export const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      { index: true, element: <HomePage /> },
      {
        path: 'formation',
        element: <FormationPage />,
      },
      { path: 'contact', element: <ContactPage /> },
      {
        path: 'login',
        element: <LoginPage />,
        loader: authRedirectLoader,
      },
      {
        path: 'signup',
        element: <SignupPage />,
        loader: authRedirectLoader,
      },
      {
        path: 'category/:categoryId',
        element: <CategoryViewPage />,
      },
      {
        path: 'subcategory/:subcategoryId',
        element: <SubcategoryViewPage />,
      },
      {
        path: 'course/:subcategoryId/:courseId',
        element: <CourseViewPage />,
      },
      {
        path: 'payment/success',
        element: <PaymentSuccessPage />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'payment/cancel',
        element: <PaymentCancelPage />,
        errorElement: <ErrorBoundary />,
      },
    ],
  },
  {
    path: '/admin',
    element: <AdminLayout />,
    loader: adminLoader,
    errorElement: <Navigate to="/login?from=/admin" replace />,
    children: [
      { index: true, element: <AdminDashboardPage /> },
      { path: 'categories', element: <AdminCategoriesPage /> },
      { path: 'categories/:categoryId/subcategories', element: <AdminSubcategoriesPage /> },
      { path: 'subcategories', element: <AdminSubcategoriesPage /> },
      { path: 'subcategories/:subcategoryId/chapters', element: <AdminChaptersPage /> },
      { path: 'chapters', element: <AdminChaptersPage /> },
      { path: 'chapters/:chapterId/courses', element: <AdminCoursesPage /> },
      { path: 'courses', element: <AdminCoursesPage /> },
      { path: 'users', element: <AdminUsersPage /> },
    ],
  },
]);
